import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ShippingServiceFields } from "~/components/shipping-service-fields/shipping-service-fields";
import { Button } from "~/components/ui/button";
import { Form } from "~/components/ui/form";
import {
  type UsedCouriersQuery,
  useUsedCouriersQuery,
} from "~/graphql/graphql-new.gen";
import { useUpdateSellerMutation } from "~/graphql/graphql.gen";
import { brandState } from "~/stores/brand";

import { useVisibleCouriers } from "./use-default-couriers";

export type SelectedCourier = UsedCouriersQuery["usedCouriers"][number] & {
  selected: boolean;
};

export type OnboardingStepShippingDeliveryServiceProps = {
  goToNextSubStepOrStep: () => void;
};

export type OnboardingStepShippingDeliveryServiceForm = {
  services: SelectedCourier[];
};

export const OnboardingStepShippingDeliveryService: React.FC<
  OnboardingStepShippingDeliveryServiceProps
> = ({ goToNextSubStepOrStep }: OnboardingStepShippingDeliveryServiceProps) => {
  const brand = useAtomValue(brandState);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { getVisibleCouriers } = useVisibleCouriers();

  const { mutateAsync: updateSellerMutation } = useUpdateSellerMutation();

  const form = useForm<OnboardingStepShippingDeliveryServiceForm>({
    defaultValues: async () => {
      return {
        services: await getVisibleCouriers(),
      };
    },
  });

  if (!brand) return;

  const onSubmit = async (data: OnboardingStepShippingDeliveryServiceForm) => {
    await updateSellerMutation({
      updateSeller: {
        used_couriers: data.services
          .filter((service) => service.selected === true)
          .map((service) => service.slug),
      },
    });
    queryClient.invalidateQueries({
      queryKey: useUsedCouriersQuery.getKey(),
    });
    goToNextSubStepOrStep();
  };

  return (
    <>
      <div className="mb-10 flex flex-col gap-10 sm:gap-2">
        <h2 className="mt-10 text-2xl font-bold sm:mt-20 sm:text-3.5xl">
          {t("onboarding.shipping.sub_steps.2.title")}
        </h2>
        <p className="text-sm leading-5.5 text-gray-700">
          {t("onboarding.shipping.sub_steps.2.subtitle")}
        </p>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-10"
        >
          <ShippingServiceFields
            shippingCountryCode={brand.shipping_country ?? "FR"}
          />

          <Button type="submit" size="lg" className="self-start">
            {t("continue")}
          </Button>
        </form>
      </Form>
    </>
  );
};

OnboardingStepShippingDeliveryService.displayName =
  "OnboardingStepShippingDeliveryService";
